import { useState } from 'react';

import { DateTime } from 'luxon';
import { GetServerSidePropsContext, GetStaticPropsResult } from 'next';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import { Col, Container, Row } from 'react-bootstrap';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import { findResultsState } from 'react-instantsearch-dom/server';

import styles from './index.module.scss';
import AlgoliaPagination from '@website/components/search/algolia/AlgoliaPagination';
import SeoMetaTags from '@website/components/seoMetaTags';
import { AlgoliaSearchClient } from '@website/components/trips/search/AlgoliaSearchClient';
import DepartingGroupsResults from '@website/components/trips/search/DeparturesGroupResults';
import TripSearchForm from '@website/components/trips/search/TripSearchForm';
import {
  createURLDepartures,
  DEBOUNCE_TIME,
  searchStateToUrlDepartures as searchStateToUrl,
  urlToSearchState,
} from '@website/components/trips/search/utils/saveState';


const ALGOLIA_INDEX_NAME = 'departingGroups' as const;

const SearchPageTabs = dynamic(
  () =>
    import('@website/components/trips/search/SearchPageTabs').then(
      (mod) => mod.default,
    ),
  { ssr: false },
);

const SalesPopupProvider = dynamic(
  () =>
    import('@website/components/SalesPopupProvider').then(
      (mod) => mod.SalesPopupProvider,
    ),
  { ssr: false },
);

type Props = {
  searchState: {};
  resultsState: {};
};

export async function getServerSideProps({
  query,
}: GetServerSidePropsContext): Promise<GetStaticPropsResult<Props>> {
  const searchState = urlToSearchState(query);

   
  const resultsState = await findResultsState(DeparturesPage as any, {
    searchStateProps: searchState,
    indexName: ALGOLIA_INDEX_NAME,
    searchClient: AlgoliaSearchClient,
  });

  return {
    props: {
      resultsState: JSON.parse(JSON.stringify(resultsState)),
      searchState,
    },
  };
}

export default function DeparturesPage({
  searchState: searchStateProps,
  resultsState,
  ...props
}: Props) {
  const [searchState, setSearchState] = useState(searchStateProps);
  const [debouncedSetState, setDebouncedSetState] = useState(null);

  const onSearchStateChange = (updatedSearchState: Record<string, unknown>) => {
    clearTimeout(debouncedSetState);

    setDebouncedSetState(
      setTimeout(() => {
        Router.push(
          searchStateToUrl(updatedSearchState),
          searchStateToUrl(updatedSearchState),
          { shallow: true },
        );
      }, DEBOUNCE_TIME),
    );

    setSearchState(updatedSearchState);
  };

  const startOfCurrentHour = DateTime.utc().startOf('hour').toMillis() / 1000;

  return (
    <>
      <SeoMetaTags
        title="Skyhook | Trips"
        description="Once in a lifetime adventures, made possible by local guides."
        showWebsiteName={false}
      />

      <Container fluid className="mt-3 px-lg-5">
        <Row>
          <Col xl="8">
            <h1 className={styles.title}>Find your next adventure</h1>
            {/* <p className="lead text-muted">{data.content && data.content}</p> */}
          </Col>
        </Row>
      </Container>

      <Container fluid className="mb-5 px-lg-5">
        <InstantSearch
          searchClient={AlgoliaSearchClient}
          indexName={ALGOLIA_INDEX_NAME}
          searchState={searchState}
          onSearchStateChange={onSearchStateChange}
          createURL={createURLDepartures}
          resultsState={resultsState}
          {...props}
        >
          <Configure
            filters={`trip.active:"ACTIVE" AND private:false AND remainingSpaces>0 AND spacesBooked>0 AND startTimestamp>${startOfCurrentHour}`}
            facetingAfterDistinct
            hitsPerPage={50}
          />
          <Row>
            <Col lg="3" className="pt-3">
              <TripSearchForm />
            </Col>

            <Col lg="9" className="pt-3">
              <SearchPageTabs activeKey="departures" />

              <DepartingGroupsResults />
              <AlgoliaPagination />
            </Col>
          </Row>
        </InstantSearch>
      </Container>
      <SalesPopupProvider bottomPosition={10} />
    </>
  );
}
